import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/lib/salesData";

/* TestimonialsSection — Depoimentos com layout em grade e destaque no primeiro */
const getInitials = (name: string) => {
  const parts = name.split(" ");
  return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : parts[0][0];
};

export const TestimonialsSection = () => {
  const featured = TESTIMONIALS[0];
  const rest = TESTIMONIALS.slice(1);

  return (
    <section className="py-16 sm:py-24 bg-card/30">
      <div className="container max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 sm:mb-14"
        >
          <span className="text-accent text-xs font-semibold uppercase tracking-widest mb-3 block">
            Prova real
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
            Quem usa, recomenda.
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-lg mx-auto">
            Profissionais de todo o Brasil já transformaram sua rotina de plantão com a Jornada.
          </p>
        </motion.div>

        {/* Depoimento destaque */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-card border-2 border-accent/30 rounded-3xl p-6 sm:p-8 md:p-10 mb-6 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10">
            <div className="flex gap-1 mb-4">
              {[...Array(featured.stars)].map((_, i) => (
                <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-warning text-warning" />
              ))}
            </div>
            <Quote className="w-6 h-6 sm:w-8 sm:h-8 text-accent/30 mb-3 sm:mb-4" />
            <p className="text-foreground text-base sm:text-lg md:text-xl leading-relaxed italic mb-5 sm:mb-6 font-medium">
              "{featured.quote}"
            </p>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-accent to-primary text-accent-foreground font-bold text-xs sm:text-sm flex items-center justify-center shrink-0">
                {getInitials(featured.name)}
              </div>
              <div>
                <div className="font-bold text-foreground text-sm sm:text-base">{featured.name}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{featured.role}</div>
                <div className="text-xs text-accent">{featured.city}</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Grade com demais depoimentos — 1 col mobile / 2 col tablet / 4 col desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {rest.map((quote, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="bg-card border border-border rounded-2xl p-4 sm:p-5 hover:border-accent/30 transition-colors"
            >
              <div className="flex gap-1 mb-3">
                {[...Array(quote.stars)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-warning text-warning" />
                ))}
              </div>
              <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed italic mb-4">
                "{quote.quote}"
              </p>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-accent to-primary text-accent-foreground font-bold text-xs flex items-center justify-center shrink-0">
                  {getInitials(quote.name)}
                </div>
                <div>
                  <div className="font-semibold text-foreground text-xs">{quote.name}</div>
                  <div className="text-xs text-muted-foreground">{quote.role}</div>
                  <div className="text-xs text-accent">{quote.city}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
